// The hiring module's design tokens.
//
// WHY A FILE AND NOT INLINE VALUES
// Before this, every screen picked its own greys. Nine of them, near-identical,
// none deliberate — which is what "not production ready" actually looks like up
// close. A candidate moving from the welcome screen to the interview to the
// confirmation should not be able to tell they crossed a boundary.
//
// TWO SURFACES, ONE SYSTEM
// The candidate sees a dark, calm room. HR sees a bright, dense workspace. They
// are different worlds on purpose — one is a place you sit and think for half an
// hour, the other is a list you work down — but they share a scale, a rhythm and
// a type ramp so they read as one product.
//
// THE ACCENT RULE
// Maya's amber means "the system did this": her voice, her presence, anything
// inferred. Teal means "you can act on this". Semantic colours (positive,
// caution, negative) are separate again and never used decoratively. Three
// meanings, three colours; nothing gets a colour for being pretty.

export const FONT = "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif";
export const MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', monospace";

// ── The candidate's room ────────────────────────────────────────────────────
//
// Dark because the candidate's own video sits on this screen, and a bright page
// around a webcam feed makes people look worse than they are. Warm-shifted
// rather than blue-black: a cold interface for a nervous person is the wrong
// call, and the warmth ties to Maya.
export const dark = {
  bg: "#0a0f14",
  bgLift: "#0f151c",
  panel: "rgba(255,255,255,.028)",
  panelSolid: "#141b23",
  hair: "rgba(255,255,255,.07)",
  line: "rgba(255,255,255,.12)",

  ink: "#f1f5f9",
  body: "rgba(241,245,249,.80)",
  muted: "rgba(241,245,249,.55)",
  faint: "rgba(241,245,249,.34)",

  // Maya. Warm amber — the system's own voice.
  maya: "#f6c28b",
  mayaSoft: "rgba(246,194,139,.13)",
  mayaGlow: "251, 191, 116",

  // Yours. Teal — anything the candidate can act on.
  act: "#2dd4bf",
  actDeep: "#0f766e",
  actSoft: "rgba(45,212,191,.12)",

  positive: "#4ade80",
  caution: "#fbbf24",
  negative: "#fb7185",
};

// ── HR's workspace ──────────────────────────────────────────────────────────
//
// Neutrals carry a faint warm bias so the amber accent sits in the same family
// rather than on top of it. A pure blue-grey next to amber reads as two systems.
export const light = {
  bg: "#fafaf9",
  card: "#ffffff",
  panel: "#f5f5f4",
  hair: "#f0efee",
  line: "#e0dedb",

  ink: "#1c1917",
  body: "#44403c",
  muted: "#78716c",
  faint: "#a8a29e",

  maya: "#b45309",
  mayaSoft: "#fef6ec",
  act: "#0f766e",
  actSoft: "#effcf9",

  positive: "#15803d",
  positiveSoft: "#f0fdf4",
  caution: "#b45309",
  cautionSoft: "#fffbeb",
  negative: "#b91c1c",
  negativeSoft: "#fef2f2",
};

// ── Scale ───────────────────────────────────────────────────────────────────
//
// One ramp, used everywhere. Values that are not on it look like accidents,
// because they are.
export const space = { xs: 0.5, sm: 1, md: 1.5, lg: 2.5, xl: 4, xxl: 6 };

export const radius = { sm: 1.5, md: 2.5, lg: 3.5, pill: 100 };

export const type = {
  display: { fontSize: { xs: 26, md: 34 }, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1.2 },
  title:   { fontSize: { xs: 19, md: 22 }, fontWeight: 700, letterSpacing: "-.01em", lineHeight: 1.3 },
  heading: { fontSize: 16, fontWeight: 650, letterSpacing: "-.005em" },
  body:    { fontSize: 14.5, lineHeight: 1.7 },
  small:   { fontSize: 13, lineHeight: 1.6 },
  label:   { fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: ".1em", textTransform: "uppercase" },
};

// Motion. Short, and on one curve — the difference between a page that feels
// built and one that feels assembled is usually that its animations disagree
// with each other about how fast the world moves.
export const ease = "cubic-bezier(.22,1,.36,1)";
export const motion = {
  fast: `.18s ${ease}`,
  base: `.28s ${ease}`,
  slow: `.5s ${ease}`,
};

// Injected once. Keyframes cannot live in the token object, and every screen
// needing the same three animations was three copies of them.
export function injectKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("hiring-keyframes")) return;
  const style = document.createElement("style");
  style.id = "hiring-keyframes";
  style.textContent = `
    @keyframes hRise   { from { opacity:0; transform: translateY(8px) } to { opacity:1; transform:none } }
    @keyframes hFade   { from { opacity:0 } to { opacity:1 } }
    @keyframes hBreathe{ 0%,100% { opacity:.55 } 50% { opacity:1 } }
    @keyframes hSpin   { to { transform: rotate(360deg) } }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
    }
  `;
  document.head.appendChild(style);
}

/** Staggered entrance, capped so a long list does not animate for four seconds. */
export const rise = (i = 0) => ({
  animation: `hRise .34s ${ease} ${Math.min(i, 10) * 0.035}s both`,
});

export default { FONT, MONO, dark, light, space, radius, type, motion, ease, rise, injectKeyframes };
